import * as React from "react";
import { useSelector } from "react-redux";
import { AmountTier, BankAccountDTOAccountTypeEnum, FeesDTO, PaymentMethodsDTO, PaymentTypeEnum } from "@inosio/payments-sdk";
import SpinnerWithNoConfirmation from "../../../shared/Modal/spinnerWithNoConfirmation";
import AddNewCardPaymentModal from "../../../shared/Modal/AddNewCardPaymentModel";
import AddNewBankPaymentModal from "../../../shared/Modal/AddNewBankPaymentMethod";
import DialogPaymentComp from "./DialogPaymentComp";
import AddCardIcon from "../../../icons/addcard.png";
import BankIcon from "../../../icons/Bank_Icon.svg";
import CreditCardIcon from "../../../icons/CreditCardIcon.svg";
import DebitCardIcon from "../../../icons/DebitCardIcon.svg";
import GreyBankIcon from "../../../icons/GreyBankIcon.svg";
import GreyCreditCardIcon from "../../../icons/GreyCreditCardIcon.svg";
import GreyDebitCardIcon from "../../../icons/GreyDebitCardIcon.svg";
import CloseModalIcon from "../../../icons/RoundCloseIcon.svg";
import PaymentMethodIcon from "../../../icons/PaymentsMethodsIcon.svg";
import { selectPaymentState, selectPaymentFeesByResident, selectPaymentMethods, selectTransactionHistory, selectBankAccResponseDetails, selectBankCardResponseDetails, selectPaymentConfigs, selectResidentDetails } from "../sagas/residentpaymentsSlice";
import { selectAvailablePaymentMethods, selectResidentResidentFees, selectResidentNewBankAccount, selectResidentNewBankCard, selectResidentPaymentConfigs, selectAdhocBankAccount, selectAdhocBankCard, DEFAULT_ADHOC_BANK_UUID, DEFAULT_ADHOC_CARD_UUID } from "../../../pmui/residents/saga/pmResidentsSlice";
import { useInosioUser } from "../../../shared/login/useInosioUser";
import { Box, Button, Dialog, DialogContent, Grid, Link, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { TieredPricingTable } from "../../../pmui/onboarding/pricingCard";
import { getAvailablePaymentMethods, useIsPmAdhoc } from "./UseSharedMethods";
import { selectAuthState } from "../../../shared/sagas/auth/authSlice";
import HelpIcon from "@mui/icons-material/Help";
import { UNREG_DEBIT_CARD_GENERIC } from "../../../utils/constants";

interface PropsType {
  irpPaymentClass: string;
  setSelectedPaymentHandler: (fees: number, lastFour: string, uuid: string, tiers?: AmountTier[], rawFeeDTO? : FeesDTO) => void;
  targetDate: number;
  smallVersion?: boolean;
  availablePaymentMethods?: PaymentTypeEnum[];
}

export default function ModalContentsPayMethods(props: PropsType) {
  const isOnMobileDevice = useMediaQuery("(max-width:600px)");
  const [isInosioUser] = useInosioUser();
  const isPmAdhoc = useIsPmAdhoc();
  const { ipaUser } = useSelector(selectAuthState);
  const residentsDetails = useSelector(selectResidentDetails);
  const { isFetchingPaymentMethods, RawPaymentFeeByResident, isFetchingPaymentFeesByResident, isFetchingCreateBankAccount, isFetchingCreateBankCard } = useSelector(selectPaymentState);
  const paymentFeesByResident = useSelector(isPmAdhoc ? selectResidentResidentFees: selectPaymentFeesByResident);
  const paymentConfigs = useSelector(isPmAdhoc ? selectResidentPaymentConfigs: selectPaymentConfigs);
  const pastTransactions = useSelector(selectTransactionHistory);
  const newlyAddedBankAccount = useSelector(isPmAdhoc ? selectResidentNewBankAccount: selectBankAccResponseDetails);
  const newlyAddedBankCard = useSelector(isPmAdhoc ? selectResidentNewBankCard: selectBankCardResponseDetails);
  const [selectedPayMethodUuid, setSelectedPayMethodUuid] = React.useState("");
  const [savedPayMethods, setSavedPayMethods] = React.useState<any[]>([]);
  const [openNewPayMethodModal, setOpenNewPayMethodModal] = React.useState(false);
  const [openDebitCardModal, setOpenDebitCardModal] = React.useState(false);
  const [openCreditCardModal, setOpenCreditCardModal] = React.useState(false);
  const [openBankModal, setOpenBankModal] = React.useState(false);
  const [showACHDisabledText, setShowACHDisabledText] = React.useState(false);
  const [showCardsDisabledText, setShowCardsDisabledText] = React.useState(false);

  const adhocPaymentMethods = useSelector(selectAvailablePaymentMethods);
  const savedPaymentMethods = useSelector(selectPaymentMethods);
  const [currentPaymentMethods, setCurrentPaymentMethods] = React.useState<PaymentMethodsDTO>({BankAccounts: [], BankCards: []});
  React.useEffect(() => {
    if ((ipaUser?.currentProfile?.isActing && !isInosioUser) || isPmAdhoc) {
      setCurrentPaymentMethods({
        BankAccounts: [...(adhocPaymentMethods?.BankAccounts ?? []), ...(savedPaymentMethods?.BankAccounts ?? [])], 
        BankCards: [...(adhocPaymentMethods?.BankCards ?? []), ...(savedPaymentMethods?.BankCards ?? [])]
      });
    } else {
      setCurrentPaymentMethods({ 
        BankAccounts: savedPaymentMethods?.BankAccounts ?? [], 
        BankCards: savedPaymentMethods?.BankCards ?? []
      })
    }
  }, [adhocPaymentMethods, savedPaymentMethods])
  const adhocBankAccount = useSelector(selectAdhocBankAccount);
  const adhocBankCard = useSelector(selectAdhocBankCard);

  //Get availablePaymentMethods
  const availablePaymentMethods: PaymentTypeEnum[] = props.availablePaymentMethods ?? getAvailablePaymentMethods(props.targetDate, paymentConfigs?.PaymentTypeConfigs, paymentFeesByResident, residentsDetails);

  //extract registered payment methods
  React.useEffect(() => {
    let bankAccountsWrappedWithFees: filteredBankAccountsPaymentsType[] = [];
    let bankCardsWrappedWithFees: any[] = [];
    if (currentPaymentMethods && paymentFeesByResident) {
      bankAccountsWrappedWithFees = [
        ...bankAccountsWrappedWithFees,
        ...currentPaymentMethods.BankAccounts
        // .filter((method) => !!availablePaymentMethods.find((apm) => apm.toString() === "ACH"))
        .map((method) => {
          const payMethodFee = getPaymentMethodFee(paymentFeesByResident, props.irpPaymentClass, "ACH");
          const payMethodFeeDto = getPaymentMethodFee(RawPaymentFeeByResident, props.irpPaymentClass, "ACH");
          return { 
            ...method, 
            feeAmount: payMethodFee?.FeeAmount, 
            tiers: payMethodFee?.Tiers, 
            PaidBy: payMethodFee?.PaidBy, 
            rawFeeDTO: payMethodFeeDto as FeesDTO,
            Disabled: !availablePaymentMethods.some((apm) => apm.toString() === "ACH"),
          } as filteredBankAccountsPaymentsType;
        }),
      ];

      bankCardsWrappedWithFees = [
        ...bankCardsWrappedWithFees,
        ...currentPaymentMethods.BankCards
        // .filter((method) => !!availablePaymentMethods.find((apm) => apm.toString() === method.CardType.toString()))
        .map((method) => {
          const payMethodFee = getPaymentMethodFee(paymentFeesByResident, props.irpPaymentClass, method.ProcessAsCardType);
          const payMethodFeeDto = getPaymentMethodFee(RawPaymentFeeByResident, props.irpPaymentClass, method.ProcessAsCardType);
          return { 
            ...method, 
            feeAmount: payMethodFee?.FeeAmount, 
            tiers: payMethodFee?.Tiers, 
            PaidBy: payMethodFee?.PaidBy, 
            rawFeeDTO: payMethodFeeDto as FeesDTO,
            Disabled: !availablePaymentMethods.find((apm) => apm.toString() === method.CardType.toString()),
          };
        }),
      ];
    }
    const allMethods = [...bankAccountsWrappedWithFees, ...bankCardsWrappedWithFees];

    //Determine to show 'ACH or Cards is/are restricted' message.
    setShowACHDisabledText(allMethods.some((method) => (method?.AccountType === "C" || method?.AccountType === "S") && method.Disabled));
    setShowCardsDisabledText(allMethods.some((method) => (method?.CardType === "CC" || method?.CardType === "DB") && method.Disabled));
  
    //Determine which method is auto-selected
    let payMethodUuid = selectedPayMethodUuid;
    const enabledMethods = allMethods.filter((m) => !m.Disabled);
    if (adhocBankAccount?.AccountNumber) {
      payMethodUuid = DEFAULT_ADHOC_BANK_UUID;
    } else if (adhocBankCard?.CardNumber) {
      payMethodUuid = DEFAULT_ADHOC_CARD_UUID;
    } else if (!selectedPayMethodUuid) {
      // If only one pay method exists, auto select that one
      if (enabledMethods.length === 1) {
        payMethodUuid = enabledMethods[0]?.Uuid
      } else if (enabledMethods.length > 1) {
        // If multiple and transactions exist, select the last one used for a payment
        if (pastTransactions.length > 0) {
          const lastPayment = pastTransactions?.reduce((prev, current) => (new Date(prev.TransactionDate as string) > new Date(current.TransactionDate as string) ? prev : current));
          const lastUsedPaymentMethod = lastPayment?.TransactionDetails[0]?.FromPayMethod?.Uuid;
          if (enabledMethods.find((m) => m.Uuid === lastUsedPaymentMethod)) {
            payMethodUuid = lastUsedPaymentMethod;
          }
        }

        if (!payMethodUuid) {
          //if multiple and no transactions exist, use the last one added
          const lastOneAdded = enabledMethods.reduce((prev, current) => (new Date(prev.CreatedDate) > new Date(current.CreatedDate) ? prev : current));
          payMethodUuid = lastOneAdded.Uuid;
        }
      }
    }

    setSelectedPayMethodUuid(payMethodUuid);

    const selectedPayMethod= allMethods.find((meth)=>meth?.Uuid === payMethodUuid);
    selectedPayMethod && setSelectedPaymentHandler(selectedPayMethod?.feeAmount, selectedPayMethod?.AccountLastFour, selectedPayMethod?.Uuid, selectedPayMethod?.tiers, selectedPayMethod?.rawFeeDTO);

    const sortedMethods = allMethods.sort((a: any, b: any) => (a?.Uuid === payMethodUuid ? -1 : b?.Uuid === payMethodUuid ? 1 : 0));
    setSavedPayMethods(sortedMethods);
  }, [currentPaymentMethods, paymentFeesByResident, pastTransactions, adhocBankAccount, adhocBankCard]);

  const setSelectedPaymentHandler = (feesCents: number, lastFour: string, methodUuid: string, tiers?: AmountTier[], rawFeeDTO?: FeesDTO) => {
    setSelectedPayMethodUuid(methodUuid);
    props.setSelectedPaymentHandler(feesCents, lastFour, methodUuid, tiers, rawFeeDTO);
  };

  let bankFeesAmount: any = null;
  let debitCardFeesAmount: any = null;
  let creditCardFeesAmount: any = null;
  if (paymentFeesByResident) {
    bankFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === "ACH" && feeMethod?.PaymentClass === props.irpPaymentClass);
    debitCardFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === "DB" && feeMethod?.PaymentClass === props.irpPaymentClass);
    creditCardFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === "CC" && feeMethod?.PaymentClass === props.irpPaymentClass);
  }

  const handleCloseNewPayMethodModal = () => {
    setOpenDebitCardModal(false);
    setOpenCreditCardModal(false);
    setOpenBankModal(false);
    setOpenNewPayMethodModal(false);
  }

  // anytime a new bank account is added, make default paymentMethod
  React.useEffect(() => {
    if ((isFetchingCreateBankAccount || isPmAdhoc) && newlyAddedBankAccount?.Uuid) {
      setSelectedPaymentHandler(
        // prettier-ignore
        getPaymentMethodFee(paymentFeesByResident, props.irpPaymentClass, "ACH")?.FeeAmount ?? 0,
        newlyAddedBankAccount.AccountLastFour,
        newlyAddedBankAccount.Uuid
      );
    }
  }, [isFetchingCreateBankAccount, newlyAddedBankAccount]);

  // anytime a new bank card is added, make default paymentMethod
  React.useEffect(() => {
    if ((isFetchingCreateBankCard || isPmAdhoc) && newlyAddedBankCard?.Uuid) {
      setSelectedPaymentHandler(
        // prettier-ignore
        getPaymentMethodFee(paymentFeesByResident, props.irpPaymentClass, newlyAddedBankCard.CardType)?.FeeAmount ?? 0,
        newlyAddedBankCard.AccountLastFour,
        newlyAddedBankCard.Uuid
      );
    }
  }, [isFetchingCreateBankCard, newlyAddedBankCard]);

  const [showDBTieredPricing, setShowDBTieredPricing] = React.useState(false);
  const [showCCTieredPricing, setShowCCTieredPricing] = React.useState(false);
  const [showACHTieredPricing, setshowACHTieredPricing] = React.useState(false);

  const isBankFeeCompletelyPmIncurred = !!((bankFeesAmount?.Tiers?.length && bankFeesAmount.Tiers.every((tf: { PaidBy: string }) => tf.PaidBy === "P")) || (bankFeesAmount && bankFeesAmount.PaidBy === "P" && !bankFeesAmount.Tiers.length));
  const isDbFeeCompletelyPmIncurred = !!(
    (debitCardFeesAmount?.Tiers?.length && debitCardFeesAmount.Tiers?.every((tf: { PaidBy: string }) => tf.PaidBy === "P")) ||
    (debitCardFeesAmount && debitCardFeesAmount.PaidBy === "P" && !debitCardFeesAmount.Tiers.length)
  );
  const isCcFeeCompletelyPmIncurred = !!(
    (creditCardFeesAmount?.Tiers?.length && creditCardFeesAmount.Tiers?.every((tf: { PaidBy: string }) => tf.PaidBy === "P")) ||
    (creditCardFeesAmount && creditCardFeesAmount.PaidBy === "P" && !creditCardFeesAmount.Tiers.length)
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ flexGrow: 1, minHeight: 230 }}>
        {isFetchingPaymentMethods || isFetchingPaymentFeesByResident ? (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            style={{ marginTop: '50px' }}
          >
            <SpinnerWithNoConfirmation />
          </Stack>
        ) : currentPaymentMethods && (currentPaymentMethods?.BankAccounts.length !== 0 || currentPaymentMethods?.BankCards.length !== 0) ? (
          <React.Fragment>
            <DialogPaymentComp
              availablePaymentMethods={availablePaymentMethods}
              paymentUuid={selectedPayMethodUuid}
              setPaymentUuid={setSelectedPayMethodUuid}
              methods={savedPayMethods}
              setSelectedPayHandler={setSelectedPaymentHandler} 
              irpPaymentClass={props.irpPaymentClass}
            />

            {showACHDisabledText || showCardsDisabledText ? (
              <Typography
                style={{
                  color: "#14142B",
                  fontSize: "16px",
                  lineHeight: "22px",
                  fontFamily: "'Work Sans', sans-serif",
                  fontWeight: 400,
                  textAlign: "center"
                }}
              >
                {showACHDisabledText && showCardsDisabledText ? 'All methods are restricted.': showACHDisabledText ? 'ACH is restricted.' : 'Cards are restricted.'}
                <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={`Contact your Property Manager to request the ability to ${showACHDisabledText && showCardsDisabledText ? 'pay': showACHDisabledText ? 'use ACH': 'use cards'}.`} placement="bottom">
                  <HelpIcon sx={{ fontSize: 17, color: "#C4C4C4", cursor: "pointer" }} />
                </Tooltip>
              </Typography>
            ) : <></>}

          </React.Fragment>
        ) : (
          <Stack direction="column" justifyContent="center" alignItems="center" spacing={2} sx={{ marginBottom: "2%" }}>
            <img src={PaymentMethodIcon} alt="Payment" height={"164px"} />
          </Stack>
        )}
      </div>
      <div style={{ marginTop: "-35px" }}>
        <img style={{ cursor: "pointer" }} src={AddCardIcon} onClick={() => setOpenNewPayMethodModal(true)} alt="add card" id="otpAddPmtMethodBtn" />
      </div>

      {openNewPayMethodModal && (
        <Dialog
          fullScreen={isOnMobileDevice}
          open={openNewPayMethodModal}
          onClose={handleCloseNewPayMethodModal}
          fullWidth={true}
          disableEnforceFocus
          hideBackdrop={true}
          PaperProps={{
            style: {
              pointerEvents: "auto",
              height: !isOnMobileDevice ? "700px" : "100%",
              borderRadius: isOnMobileDevice ? "none": "24px",
            },
          }}
        >
          <DialogContent>
            <Stack direction={"column"} sx={{ height: "100%" }}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography id="payment-method-title">Payment Method Type</Typography>
                  <img src={CloseModalIcon} alt="close" style={{ cursor: "pointer" }} onClick={() => setOpenNewPayMethodModal(false)} />
                </Stack>
              </Box>

              <Grid container item xs={12} spacing={1} justifyContent={"center"} sx={{marginTop: "20%"}}>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{
                      width: "240px",
                      height: "max-content",
                      position: "absolute",
                      top: "90px",
                      left: "0",
                      zIndex: "9",
                      background: "#ffffff",
                      border: "2px solid #eeeeee",
                      borderRadius: "5px",
                      display: showDBTieredPricing ? undefined : "none",
                    }}
                  >
                    {!!debitCardFeesAmount?.Tiers && <TieredPricingTable tiers={debitCardFeesAmount.Tiers} PaymentType={PaymentTypeEnum.Db} />}
                  </Box>
                  {openDebitCardModal && (
                    <AddNewCardPaymentModal
                      handleCloseCardModal={handleCloseNewPayMethodModal}
                      openCardModal={openDebitCardModal}
                      handleCloseGoBackCardModal={handleCloseNewPayMethodModal}
                      smallVersion={props.smallVersion}
                      title={'Debit'}
                    />
                  )}
                  <div>
                    <img src={availablePaymentMethods.includes(PaymentTypeEnum.Db) ? DebitCardIcon : GreyDebitCardIcon} alt="card" />
                  </div>
                  <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                    {isDbFeeCompletelyPmIncurred ? (
                      <>Free</>
                    ) : props.irpPaymentClass === "VAP" && debitCardFeesAmount?.Tiers?.length ? (
                      <Link
                        onMouseEnter={(event: React.SyntheticEvent) => {
                          setShowDBTieredPricing(true);
                        }}
                        onMouseLeave={(event: React.SyntheticEvent) => {
                          setShowDBTieredPricing(false);
                        }}
                      >
                        View fees
                        <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={UNREG_DEBIT_CARD_GENERIC} placement="bottom">                        
                        <HelpIcon sx={{ fontSize: 17, color: "#C4C4C4", cursor: "pointer" }} />              
                      </Tooltip>
                      </Link>
                    ) : (
                      <>Fees: ${debitCardFeesAmount && (debitCardFeesAmount.FeeAmount / 100).toFixed(2)} - ${creditCardFeesAmount && (creditCardFeesAmount.FeeAmount / 100).toFixed(2)}
                      <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={UNREG_DEBIT_CARD_GENERIC} placement="bottom">                        
                        <HelpIcon sx={{ fontSize: 17, color: "#C4C4C4", cursor: "pointer" }} />
                      </Tooltip>
                      </>
                    )}
                  </Typography>
                  <div>
                    <Button disabled={!availablePaymentMethods.includes(PaymentTypeEnum.Db)} style={{ marginBottom: "8px" }} variant="contained" id="add-card-btn" onClick={() => setOpenDebitCardModal(true)}>
                      Debit Card
                    </Button>
                  </div>
                  {!availablePaymentMethods.includes(PaymentTypeEnum.Db) && (
                    <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                      Debit card not accepted
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{
                      width: "240px",
                      height: "max-content",
                      position: "absolute",
                      top: "90px",
                      left: "0",
                      zIndex: "9",
                      background: "#ffffff",
                      border: "2px solid #eeeeee",
                      borderRadius: "5px",
                      display: showCCTieredPricing ? undefined : "none",
                    }}
                  >
                    {!!creditCardFeesAmount?.Tiers && <TieredPricingTable tiers={creditCardFeesAmount.Tiers} />}
                  </Box>
                  {openCreditCardModal && (
                    <AddNewCardPaymentModal
                      handleCloseCardModal={handleCloseNewPayMethodModal}
                      openCardModal={openCreditCardModal}
                      handleCloseGoBackCardModal={handleCloseNewPayMethodModal}
                      smallVersion={props.smallVersion}
                      title={'Credit'}
                    />
                  )}
                  <div>
                    <img src={availablePaymentMethods.includes(PaymentTypeEnum.Cc) ? CreditCardIcon : GreyCreditCardIcon} alt="card" />
                  </div>
                  <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                    {isCcFeeCompletelyPmIncurred ? (
                      <>Free</>
                    ) : props.irpPaymentClass === "VAP" && creditCardFeesAmount?.Tiers?.length ? (
                      <Link
                        onMouseEnter={(event: React.SyntheticEvent) => {
                          setShowCCTieredPricing(true);
                        }}
                        onMouseLeave={(event: React.SyntheticEvent) => {
                          setShowCCTieredPricing(false);
                        }}
                      >
                        View fees
                      </Link>
                    ) : (
                      `Fees: ${creditCardFeesAmount && (creditCardFeesAmount.FeeAmount / 100).toFixed(2)}`
                    )}
                  </Typography>
                  <div>
                    <Button disabled={!availablePaymentMethods.includes(PaymentTypeEnum.Cc)} style={{ marginBottom: "8px" }} variant="contained" id="add-card-btn" onClick={() => setOpenCreditCardModal(true)}>
                      Credit Card
                    </Button>
                  </div>
                  {!availablePaymentMethods.includes(PaymentTypeEnum.Cc) && (
                    <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                      Credit card not accepted
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{
                      width: "240px",
                      height: "max-content",
                      position: "absolute",
                      top: "90px",
                      right: "0",
                      zIndex: "9",
                      background: "#ffffff",
                      border: "2px solid #eeeeee",
                      borderRadius: "5px",
                      display: showACHTieredPricing ? undefined : "none",
                    }}
                  >
                    {!!bankFeesAmount?.Tiers && <TieredPricingTable tiers={bankFeesAmount.Tiers} />}
                  </Box>
                  {openBankModal && (
                    <AddNewBankPaymentModal
                      handleCloseBankModal={handleCloseNewPayMethodModal}
                      openBankModal={openBankModal}
                      handleCloseGoBackBankModal={handleCloseNewPayMethodModal}
                      smallVersion={props.smallVersion}
                    />
                  )}
                  <div>
                    <img src={availablePaymentMethods.includes(PaymentTypeEnum.Ach) ? BankIcon : GreyBankIcon} alt="bank" />
                  </div>
                  <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                    {isBankFeeCompletelyPmIncurred ? (
                      <>Free</>
                    ) : props.irpPaymentClass === "VAP" && bankFeesAmount?.Tiers?.length ? (
                      <Link
                        onMouseEnter={(event: React.SyntheticEvent) => {
                          setshowACHTieredPricing(true);
                        }}
                        onMouseLeave={(event: React.SyntheticEvent) => {
                          setshowACHTieredPricing(false);
                        }}
                      >
                        View fees
                      </Link>
                    ) : (
                      `Fees: ${bankFeesAmount && (bankFeesAmount.FeeAmount / 100).toFixed(2)}`
                    )}
                  </Typography>
                  <div>
                    <Button disabled={!availablePaymentMethods.includes(PaymentTypeEnum.Ach)} style={{ marginBottom: "8px" }} variant="contained" id="add-bank-modal-btn" onClick={() => setOpenBankModal(true)}>
                      Bank Account
                    </Button>
                  </div>
                  {!availablePaymentMethods.includes(PaymentTypeEnum.Ach) && (
                    <Typography id="choose-pmt-type-label" style={{ fontSize: "13px" }}>
                      ACH not accepted
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
        </Dialog>
      )}

    </div>
  )
}

export interface filteredBankAccountsPaymentsType {
  feeAmount?: number | undefined;
  AccountType?: BankAccountDTOAccountTypeEnum | undefined;
  Uuid: string;
  AccountLastFour: string;
  RoutingNumber: string;
  PaidBy?: string;
  AccountHolderFirstname?: string;
  AccountHolderLastname?: string;
  NickName?: string;
  BankName?: string;
  CurrencyCode?: string;
  tiers?: AmountTier[];
  rawFeeDTO?: FeesDTO;
  Disabled?: boolean;
}

function getPaymentMethodFee(paymentFeesByResident: FeesDTO[], irpPaymentClass: string, paymentType = "ACH") {
  return paymentFeesByResident?.find((feeMethod) => feeMethod?.PaymentType === paymentType && feeMethod?.PaymentClass === irpPaymentClass);
}
